import React, { useEffect, useState } from 'react';
import { LoadingIndicator, Alert, Skeleton } from '../ui/Primitives';
import {
  getMessageDeliverySettings,
  getOrganizationMembers,
  updateMessageDeliverySettings,
} from '../../services/organizationApi';
import './MessageDeliverySettings.css';


export default function MessageDeliverySettings({ orgMember, children, refreshVersion = 0, onBusyChange, compact = false }) {
  const canManage = Boolean(orgMember?.is_org_admin || orgMember?.is_admin);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [strategy, setStrategy] = useState('designated');
  const [memberIds, setMemberIds] = useState([]);
  const [savedSettings, setSavedSettings] = useState({
    strategy: 'designated',
    memberIds: [],
  });
  const [configured, setConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [error, setError] = useState('');
  useEffect(() => { onBusyChange?.(isLoading || isSaving); }, [isLoading, isSaving, onBusyChange]);

  useEffect(() => {
    let active = true;
    async function load() {
      setIsLoading(true);
      setError('');
      setLoadFailed(false);
      try {
        const [settings, nextMembers] = await Promise.all([getMessageDeliverySettings(), getOrganizationMembers(orgMember.organization_id)]);
        if (!active) return;
        setMembers(nextMembers);
        const nextStrategy = settings?.strategy || 'designated';
        const nextMemberIds = Array.isArray(settings?.memberIds) ? settings.memberIds.map(String) : [];
        setStrategy(nextStrategy);
        setMemberIds(nextMemberIds);
        setSavedSettings({ strategy: nextStrategy, memberIds: nextMemberIds });
        setConfigured(Boolean(settings?.configured));
      } catch (loadError) {
        if (active) {
          setLoadFailed(true);
          setError(loadError.response?.data?.error?.message || 'Message access settings could not be loaded.');
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }
    if (orgMember?.organization_id) load();
    return () => { active = false; };
  }, [orgMember?.organization_id, refreshVersion]);

  async function persist(nextStrategy, nextMemberIds) {
    if (!canManage || isSaving) return;
    const normalizedIds = nextStrategy === 'designated'
      ? [...new Set(nextMemberIds.map(String))]
      : [];
    setStrategy(nextStrategy);
    setMemberIds(normalizedIds);
    setIsSaving(true);
    setSaveStatus('saving');
    setError('');
    try {
      const settings = await updateMessageDeliverySettings({
        strategy: nextStrategy,
        memberIds: normalizedIds,
      });
      const persistedStrategy = settings?.strategy || nextStrategy;
      const persistedMemberIds = Array.isArray(settings?.memberIds) ? settings.memberIds.map(String) : [];
      setStrategy(persistedStrategy);
      setMemberIds(persistedMemberIds);
      setSavedSettings({ strategy: persistedStrategy, memberIds: persistedMemberIds });
      setConfigured(true);
      setSaveStatus('saved');
    } catch (saveError) {
      setStrategy(savedSettings.strategy);
      setMemberIds(savedSettings.memberIds);
      setSaveStatus('error');
      setError(saveError.response?.data?.error?.message || 'Message recipients could not be updated.');
    } finally {
      setIsSaving(false);
    }
  }

  function changeStrategy(nextStrategy) {
    if (!canManage || isSaving || (nextStrategy === strategy && configured)) return;
    setError('');
    setSaveStatus('idle');
    persist(nextStrategy, nextStrategy === 'designated' ? memberIds : []);
  }

  function toggleMember(memberId) {
    if (!canManage || isSaving) return;
    const id = String(memberId);
    const nextMemberIds = memberIds.includes(id)
      ? memberIds.filter(item => item !== id)
      : [...memberIds, id];
    persist('designated', nextMemberIds);
  }

  function removeMember(memberId) {
    if (isSaving) return;
    const id = String(memberId);
    const nextMemberIds = memberIds.filter(item => item !== id);
    if (nextMemberIds.length === memberIds.length) return;
    if (strategy !== 'designated' || nextMemberIds.length === 0) {
      setMemberIds(nextMemberIds);
      setSavedSettings(current => ({ ...current, memberIds: nextMemberIds }));
      setSaveStatus('saved');
      setError('');
      return;
    }
    persist('designated', nextMemberIds);
  }

  if (isLoading) {
    return <section className="message-delivery-settings"><Skeleton width="30%" height={24} /><Skeleton height={110} /></section>;
  }

  const directoryControls = {
    strategy,
    memberIds,
    canManage,
    isSaving,
    toggleRecipient: toggleMember,
    removeRecipient: removeMember,
  };

  if (compact) return <section className="message-delivery-settings message-delivery-compact" aria-label="General message recipients" aria-busy={isSaving}>
    {error && <Alert tone="danger">{error}</Alert>}
    <div className="message-delivery-options" role="group" aria-label="Who should receive general messages?">
      {[['all_active', 'Everyone on your team'], ['designated', 'Selected people']].map(([value, label]) =>
        <label key={value} className={strategy === value ? 'is-selected' : ''}>
          <input type="radio" name="message-delivery-strategy" value={value} checked={strategy === value}
            disabled={!canManage || isSaving || loadFailed} onChange={() => changeStrategy(value)} />
          <span><strong>{label}</strong></span>
        </label>
      )}
    </div>
    {strategy === 'designated' && !loadFailed && <fieldset className="message-access-members" disabled={isSaving || !canManage}>
      <legend>Choose recipients</legend>
      {members.length > 6 && <label className="message-access-search">Find a team member
        <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search by name or email" />
      </label>}
      {members.filter(member => members.length <= 6 || `${member.first_name || ''} ${member.last_name || ''} ${member.email || ''}`.toLowerCase().includes(search.toLowerCase())).map(member => {
        const name = `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email || 'Team member';
        return <label key={member.id} className="message-access-person">
          <input type="checkbox" checked={memberIds.includes(String(member.id))} onChange={() => toggleMember(member.id)} />
          <span><strong>{name}</strong><small>{member.email || 'No email address'}</small></span>
        </label>;
      })}
      {!members.length && <p>No team members available.</p>}
      {members.length > 6 && !members.some(member => `${member.first_name || ''} ${member.last_name || ''} ${member.email || ''}`.toLowerCase().includes(search.toLowerCase())) && <p>No matching team members.</p>}
    </fieldset>}
    {!loadFailed && !error && <p className="message-delivery-note" role="status">
      {isSaving ? <LoadingIndicator label="Saving…" />
        : strategy === 'designated' && memberIds.length === 0
          ? configured ? 'No recipients selected. General messages will not be emailed.' : 'No recipients selected. Choose who should receive general messages.'
          : configured ? 'Saved' : 'Choose a delivery option to save your preferences.'}
    </p>}
    <details className="message-delivery-help">
      <summary>How delivery works</summary>
      <p>Selected recipients can access general messages, including older messages. Email notifications require an email address and follow each person’s notification settings.</p>
      <p>Administrators retain organization-wide access. Messages addressed to a specific person go directly to that team member.</p>
      {!configured && <p>Your existing call-summary recipients remain in place until you save a delivery choice.</p>}
    </details>
  </section>;

  return <>
    <section className="message-delivery-settings" aria-labelledby="general-message-heading" aria-busy={isSaving}>
      <div className="message-delivery-heading">
        <div>
          <span className="say-eyebrow">Message delivery</span>
          <h2 id="general-message-heading">General business messages</h2>
          <p>Choose who can access General messages, including older messages. Email notifications go only to selected people with an email address. Administrators retain organization-wide access.</p>
        </div>
        {canManage && saveStatus !== 'idle' ? (
          <span className={`message-delivery-save-status ${saveStatus}`} role="status" aria-label="Message delivery save status">
            {saveStatus === 'saving' ? <LoadingIndicator label="Saving…" /> : saveStatus === 'saved' ? 'Saved' : 'Not saved'}
          </span>
        ) : null}
      </div>

      {error ? <Alert tone="danger">{error}</Alert> : null}
      {!loadFailed && !configured ? <Alert tone="info">Your existing call-summary recipients will continue receiving general messages until you change a delivery option.</Alert> : null}
      {!loadFailed && strategy === 'designated' && memberIds.length === 0 ? (
        <Alert tone="warning">No team members are selected. When saved, only administrators can view General messages; no General message contents will be emailed.</Alert>
      ) : null}

      <div className="message-delivery-options">
        <label className={strategy === 'designated' ? 'is-selected' : ''}>
          <input type="radio" name="message-delivery-strategy" value="designated" checked={strategy === 'designated'} disabled={!canManage || isSaving || loadFailed} onChange={() => changeStrategy('designated')} />
          <span><strong>Selected team members</strong><small>Grant access to the people you choose.</small></span>
        </label>
        <label className={strategy === 'all_active' ? 'is-selected' : ''}>
          <input type="radio" name="message-delivery-strategy" value="all_active" checked={strategy === 'all_active'} disabled={!canManage || isSaving || loadFailed} onChange={() => changeStrategy('all_active')} />
          <span><strong>Everyone</strong><small>Give all team members General access.</small></span>
        </label>
      </div>

      {strategy === 'designated' ? (
        <div className="message-delivery-selection-summary">
          <strong>{memberIds.length} {memberIds.length === 1 ? 'recipient' : 'recipients'} selected</strong>
          <span>Choose people below. Changes save automatically.</span>
        </div>
      ) : (
        <div className="message-delivery-selection-summary">
          <strong>All team members</strong>
          <span>Access is included automatically. Email notifications require an email address.</span>
        </div>
      )}

      {strategy === 'designated' && !loadFailed ? (
        <fieldset className="message-access-members" disabled={isSaving}>
          <legend>People with general message access</legend>
          <label className="message-access-search">Find a team member
            <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search by name or email" />
          </label>
          {members.filter(member => `${member.first_name || ''} ${member.last_name || ''} ${member.email || ''}`.toLowerCase().includes(search.toLowerCase())).map(member => {
            const name = `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email || 'Team member';
            return <label key={member.id} className="message-access-person">
              <input type="checkbox" disabled={!canManage} checked={memberIds.includes(String(member.id))} onChange={() => toggleMember(member.id)} aria-label={`Allow general message access for ${name}`} />
              <span><strong>{name}</strong><small>{member.email || 'No email address'}{member.is_org_admin || member.is_admin ? ' · Administrator retains access' : ''}</small></span>
            </label>;
          })}
          {!members.some(member => `${member.first_name || ''} ${member.last_name || ''} ${member.email || ''}`.toLowerCase().includes(search.toLowerCase())) ? <p>No matching team members.</p> : null}
        </fieldset>
      ) : null}

      <p className="message-delivery-note">Messages for a specific person are sent directly to that team member.</p>
    </section>
    {typeof children === 'function' ? children(directoryControls) : children}
  </>;
}
